import { useEffect, useMemo, useState } from 'react';
import { Flex, Col, Grid } from 'antd';
import { find, isEmpty } from 'lodash';
import useForm from 'hooks/useForm';
import styled from 'styled-components';

import { useTranslation, EI18nLanguageUI } from '@packages/utils';
import { Row } from 'components/Base/Grid';
import TrashIcon from 'assets/images/trash.svg';
import { Form, FormItem } from 'components/Base/Form';
import { FormInstance } from 'components/Base/Form/Form.types';
import { Select } from 'components/Base/Select';
import { Input } from 'components/Base/Input';
import { Image } from 'components/Base/Image';
import { Paragraph, Title } from 'components/Base/Typography';
import { NextStepButton } from 'components/Shared/RegisterAction/NewRegistrationModal/components/RegistrationContent/RegistationContent.style';
import { MobileDivider } from '../StepWrapper.style';
import useRegistrationContext, {
  LANGUAGE_TRANSLATE,
} from '../../context/NewRegistrationModalContext';
import { ELanguage, TOption } from '../../../../../../data/types/generic.types';
import { EEventAgeGroup } from '../../../../../../data/types/events.types';
import ProgressStepper from '../../components/ProgressStepper';
import BackNextButtons from '../../components/BackNextButtons';
import { convertToChildObject, convertToChildArray } from '../../utils/utils';
import RegistrationAgeCalculator from '../../components/AgeCalculator';
import { AdultWarning, CapacityWarning } from '../../components/Warnings';
import {
  chooseLanguageSecionKey,
  stepTwoKey,
} from '../../../RegistrationModal/RegistrationModal.data';

const AgeLevelWrapper = styled.div`
  padding: 8px;
  border: 1px solid #dedede;
  border-radius: 4px;
  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 16px;
  }
`;

export const AddChildButton = styled.button<{
  $disabled?: boolean;
}>`
  color: #0077a0;
  font-weight: 600;
  background: transparent;
  border: none;
  padding: 11px 0;
  line-height: 24px;
  font-size: 15px;
  font-family: Lato, sans-serif;
  cursor: pointer;
  @media (max-width: 768px) {
    margin-left: 4px;
    padding: 8px 0;
  }

  ${props =>
    props.$disabled &&
    `
      cursor: default;
      opacity: 0.5;
      `}
`;

const ParagraphWrapper = styled.div`
  p {
    @media (max-width: 768px) {
      font-size: 16px !important;
    }
  }
  @media (max-width: 768px) {
    order: 2;
  }
`;

const MobileFlex = styled(Flex)`
  @media (max-width: 768px) {
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin-bottom: 8px;
  }
`;

const MobileParagraphWrapper = styled(ParagraphWrapper)`
  @media (max-width: 768px) {
    margin-bottom: 0;
  }
`;

const RemoveChildButton = styled.button`
  color: #0077a0;
  font-weight: 600;
  background: transparent;
  border: none;
  padding: 8px 0;
  line-height: 24px;
  font-size: 15px;
  font-family: Lato, sans-serif;
  cursor: pointer;
`;

const DesktopAddChildButton = styled(AddChildButton)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileAddChildWrapper = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    text-align: left;
  }
`;

const DesktopRemoveChildButton = styled(RemoveChildButton)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const DesktopPlaceholder = styled(Paragraph)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const ChildInputWrapper = styled.div`
  @media (max-width: 768px) {
    margin-bottom: 4px;
  }
`;

const ChildNameLabel = styled.span`
  font-family: Lato, sans-serif;
  font-size: 16px;
  @media (max-width: 768px) {
    font-family: Lato, sans-serif !important;
    font-size: 16px;
    margin: 0;
    padding: 0;
  }
`;

const AgeLevelLabel = styled.span`
  margin-bottom: 4px;
  display: flex;
  @media (max-width: 768px) {
    font-family: Lato, sans-serif !important;
    font-size: 16px;
    padding: 0;
  }
`;

const { useBreakpoint } = Grid;

const ChildInput = ({
  onAddChild,
  options,
  index,
  childDetails,
  childrenValues,
  setChildrenValues,
  form,
  isSubmitting,
}: {
  onAddChild: () => void;
  options: { label: string; value: string }[];
  index: number;
  childDetails: { ageGroup: string; name: string; showWarning: boolean };
  childrenValues: { ageGroup: string; name: string; showWarning: boolean }[];
  setChildrenValues: React.Dispatch<
    React.SetStateAction<
      { ageGroup: string; name: string; showWarning: boolean }[]
    >
  >;
  form: FormInstance;
  isSubmitting: boolean;
}) => {
  const { t: tStepTwo } = useTranslation({
    keyPrefix: stepTwoKey,
    lng: 'translate',
  });
  const { t: tError } = useTranslation({
    keyPrefix: 'descriptions.validation-errors',
    lng: 'translate',
  });
  const {
    setChildrenInformation,
    registrationValues,
    englishGroupCapacities,
    spanishGroupCapacities,
  } = useRegistrationContext();
  const lang = registrationValues.userLanguage;
  const selectedLanguage = LANGUAGE_TRANSLATE[lang as EI18nLanguageUI];
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<
    EEventAgeGroup | undefined
  >(childDetails.ageGroup as EEventAgeGroup);
  const [currentCapacity, setCurrentCapacity] = useState<
    | {
        ageGroup: EEventAgeGroup;
        full: boolean;
      }
    | undefined
  >(undefined);
  const screens = useBreakpoint();

  const onAgeGroupChange = (value: EEventAgeGroup) => {
    const details = {
      ...childDetails,
      ageGroup: value,
      showWarning: true,
    };
    const updatedValues = [...childrenValues];
    updatedValues[index] = details;
    setChildrenValues([...updatedValues]);
    setSelectedAgeGroup(value);
  };

  const onCloseWarning = () => {
    const details = {
      ...childDetails,
      showWarning: false,
    };
    const updatedValues = [...childrenValues];
    updatedValues[index] = details;
    setChildrenValues([...updatedValues]);
  };

  const handleInputChange = (event: { target: { value: string } }) => {
    const details = {
      ...childDetails,
      name: event.target.value,
    };
    const updatedValues = [...childrenValues];
    updatedValues[index] = details;
    setChildrenValues([...updatedValues]);
  };

  const updateFormAndContext = (updatedValues: typeof childrenValues) => {
    const childrenFieldInformation = convertToChildObject(updatedValues);
    form.setFieldsValue(childrenFieldInformation);
    setChildrenInformation(convertToChildArray(childrenFieldInformation));
  };

  const onRemoveChild = () => {
    const updatedValues = [...childrenValues];
    updatedValues.splice(index, 1);

    if (options.length === 1) {
      // Single age group handling
      updatedValues.forEach((child, idx) => {
        form.setFields([
          {
            name: `child-name-${idx}`,
            value: child.name,
            errors: undefined,
          },
          {
            name: `child-ageGroup-${idx}`,
            value: child.ageGroup,
            errors: undefined,
          },
        ]);
      });
    } else {
      // Multiple age groups handling
      const formFields: any[] = [];
      formFields.push(
        { name: `child-name-${index}`, value: undefined },
        { name: `child-ageGroup-${index}`, value: undefined },
      );
      updatedValues.forEach((child, idx) => {
        formFields.push(
          {
            name: `child-name-${idx}`,
            value: child.name,
            errors: undefined,
          },
          {
            name: `child-ageGroup-${idx}`,
            value: child.ageGroup || undefined,
            errors: undefined,
          },
        );
      });
      form.setFields(formFields);
    }
    setChildrenValues(updatedValues);
    updateFormAndContext(updatedValues);
  };

  useEffect(() => {
    const capacity = find(
      selectedLanguage === ELanguage.ENGLISH
        ? englishGroupCapacities
        : spanishGroupCapacities,
      {
        ageGroup: selectedAgeGroup,
      },
    );
    setCurrentCapacity(capacity);
  }, [selectedAgeGroup]);

  return (
    <ChildInputWrapper key={`child-form-${index}`}>
      <Row
        gutter={[
          { xs: 0, sm: 16 },
          { xs: 0, sm: 16 },
        ]}
      >
        {index !== 0 && !screens.md && (
          <Col xs={24}>
            <MobileDivider />
            <MobileFlex justify="space-between" align="center">
              <MobileParagraphWrapper>
                <Paragraph className="mb-0">
                  {tStepTwo('ADD_CHILD_IN_AGE_GROUP')}
                </Paragraph>
              </MobileParagraphWrapper>
              <RemoveChildButton onClick={onRemoveChild}>
                <Image alt="Remove Child" src={TrashIcon} />
              </RemoveChildButton>
            </MobileFlex>
          </Col>
        )}
        <Col xs={childrenValues.length === 1 ? 24 : 24} md={8}>
          {options.length === 1 ? (
            <>
              <Paragraph>
                {index === 0 ? (
                  <AgeLevelLabel>{tStepTwo('AGE_LEVEL')}</AgeLevelLabel>
                ) : (
                  ''
                )}
              </Paragraph>
              {index === 0 && (
                <AgeLevelWrapper>{options[0].label}</AgeLevelWrapper>
              )}
            </>
          ) : (
            <FormItem
              label={<ChildNameLabel>{tStepTwo('AGE_LEVEL')}</ChildNameLabel>}
              name={`child-ageGroup-${index}`}
              rules={[
                {
                  validator: (_, value) => {
                    if (!value && isSubmitting) {
                      return Promise.reject(
                        new Error(tError('THIS_FIELD_IS_REQUIRED')),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Select
                onChange={onAgeGroupChange}
                options={options}
                placeholder={tStepTwo('AGE_LEVEL')}
                disabled={!selectedLanguage}
                getPopupContainer={trigger => trigger.parentNode}
                className="w-100"
              />
            </FormItem>
          )}
        </Col>
        <Col xs={childrenValues.length === 1 ? 24 : 24} md={11}>
          <FormItem
            label={
              <ChildNameLabel>
                {`${tStepTwo('CHILD_NAME')} ${index + 1}`}
              </ChildNameLabel>
            }
            name={`child-name-${index}`}
            key={`child-name-${index}`}
            rules={[
              {
                validator: (_, value) => {
                  if (!value && isSubmitting) {
                    return Promise.reject(
                      new Error(tError('THIS_FIELD_IS_REQUIRED')),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              placeholder={tStepTwo('ENTER_NAME')}
              onChange={handleInputChange}
            />
          </FormItem>
        </Col>
        <Col xs={2} md={5}>
          <DesktopPlaceholder>&nbsp;</DesktopPlaceholder>
          {index === 0 ? (
            <DesktopAddChildButton onClick={onAddChild}>
              + {tStepTwo('ADD_CHILD')}
            </DesktopAddChildButton>
          ) : (
            <DesktopRemoveChildButton onClick={onRemoveChild}>
              <Image alt="Remove Child" src={TrashIcon} />
            </DesktopRemoveChildButton>
          )}
        </Col>
      </Row>
      {currentCapacity?.full &&
        childDetails.showWarning &&
        selectedAgeGroup && <CapacityWarning onClose={onCloseWarning} />}
    </ChildInputWrapper>
  );
};

const TitleWrapper = styled.div`
  @media (max-width: 768px) {
    order: 1;
  }
`;

const AgeCalculatorWrapper = styled.div`
  order: 0;
  @media (max-width: 768px) {
    margin-top: 0;
    order: 0;
  }
`;

const AdultWarningWrapper = styled.div`
  @media (max-width: 768px) {
    order: 3;
  }
`;

const StyledForm = styled(Form)`
  flex-grow: 1;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    order: 4;
  }
`;

const ChildInformation = () => {
  const [childrenValues, setChildrenValues] = useState<
    { ageGroup: string; name: string; showWarning: boolean }[]
  >([]);
  const {
    eventDetails,
    registrationValues,
    childrenInformation,
    setChildrenInformation,
    stepper,
    setStepper,
    englishGroupCapacities,
    spanishGroupCapacities,
  } = useRegistrationContext();
  const { t: tEnum } = useTranslation({ lng: 'translate' });
  const { t: tButton } = useTranslation({
    keyPrefix: chooseLanguageSecionKey,
    lng: 'translate',
  });
  const { t: tStepTwo } = useTranslation({
    keyPrefix: stepTwoKey,
    lng: 'translate',
  });
  const { form } = useForm();
  const lang = registrationValues.userLanguage;
  const selectedLanguage = LANGUAGE_TRANSLATE[lang as EI18nLanguageUI];
  const [showCapacityWarning, setShowCapacityWarning] =
    useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { ageGroupOptions } = eventDetails;

  const ageGroupOptionsArr = useMemo(() => {
    if (!selectedLanguage) return [];
    const options: TOption[] = [];
    const language = selectedLanguage as ELanguage;
    if (ageGroupOptions) {
      options.push(...ageGroupOptions[language]);
    }
    return options.map(age => ({
      ...age,
      label: tEnum(`enums.age-group.${age.label}`),
    }));
  }, [selectedLanguage]);

  const onAddChild = () => {
    setIsSubmitting(false);
    const defaultAgeGroup =
      ageGroupOptionsArr.length === 1 ? ageGroupOptionsArr[0].value : '';
    const newChild = {
      ageGroup: defaultAgeGroup,
      name: '',
      showWarning: false,
    };
    const updatedValues = [...childrenValues, newChild];
    setChildrenValues(updatedValues);
    const newChildIndex = updatedValues.length - 1;
    if (ageGroupOptionsArr.length === 1) {
      form.setFields([
        {
          name: `child-name-${newChildIndex}`,
          value: '',
          errors: undefined,
        },
        {
          name: `child-ageGroup-${newChildIndex}`,
          value: defaultAgeGroup,
          errors: undefined,
        },
      ]);
    } else {
      form.setFields([
        {
          name: `child-name-${newChildIndex}`,
          value: '',
          errors: undefined,
        },
        {
          name: `child-ageGroup-${newChildIndex}`,
          value: undefined,
          errors: undefined,
        },
      ]);
    }
    const childrenFieldInformation = convertToChildObject(updatedValues);
    form.setFieldsValue(childrenFieldInformation);
    setChildrenInformation(convertToChildArray(childrenFieldInformation));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const values = await form.validateFields();
      const arrayValues = convertToChildArray(values);
      setChildrenInformation(arrayValues);
      setStepper(stepper + 1);
    } catch (e) {
      // do nothing
    }
  };

  const onBack = async () => {
    try {
      const values = await form.getFieldsValue();
      const arrayValues = convertToChildArray(values);
      setChildrenInformation(arrayValues);
      setStepper(stepper - 1);
    } catch (e) {
      // do nothing
    }
  };

  // Set default value if single age
  useEffect(() => {
    if (isEmpty(childrenInformation)) {
      if (ageGroupOptionsArr.length === 1) {
        const initialValue = {
          ageGroup: ageGroupOptionsArr[0].value,
          name: '',
          showWarning: false,
        };
        setChildrenValues([initialValue]);
      } else {
        setChildrenValues([{ ageGroup: '', name: '', showWarning: false }]);
      }
    } else {
      setChildrenValues([...childrenInformation]);
      const childrenFieldInformation =
        convertToChildObject(childrenInformation);
      form.setFieldsValue(childrenFieldInformation);
    }
  }, []);

  // Set warning if the only age group is full
  useEffect(() => {
    if (
      selectedLanguage === ELanguage.SPANISH &&
      spanishGroupCapacities &&
      Object.keys(spanishGroupCapacities).length === 1
    ) {
      Object.keys(spanishGroupCapacities).forEach(key => {
        if (spanishGroupCapacities[key].full) {
          setShowCapacityWarning(true);
        }
      });
    } else if (
      selectedLanguage === ELanguage.ENGLISH &&
      englishGroupCapacities &&
      Object.keys(englishGroupCapacities).length === 1
    ) {
      Object.keys(englishGroupCapacities).forEach(key => {
        if (englishGroupCapacities[key].full) {
          setShowCapacityWarning(true);
        }
      });
    }
  }, [englishGroupCapacities, spanishGroupCapacities]);

  return (
    <Flex vertical className="mb-4 h-100">
      <ProgressStepper />
      <MobileDivider />
      <AgeCalculatorWrapper data-cy="registration-age-calculator">
        <RegistrationAgeCalculator />
      </AgeCalculatorWrapper>
      {ageGroupOptionsArr.length > 1 && (
        <AdultWarningWrapper data-cy="registration-adult-warning">
          <AdultWarning />
        </AdultWarningWrapper>
      )}
      <TitleWrapper>
        <Title style={{ fontSize: '24px' }}>
          {tStepTwo('CHILD_INFORMATION')}
        </Title>
      </TitleWrapper>
      <ParagraphWrapper>
        <Paragraph className="mb-2">
          {tStepTwo('ADD_CHILD_IN_AGE_GROUP')}
        </Paragraph>
      </ParagraphWrapper>
      <StyledForm
        form={form}
        style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
      >
        {childrenValues.map((childDetails, index) => (
          <ChildInput
            // eslint-disable-next-line react/no-array-index-key
            key={`child-input-${index}`}
            onAddChild={onAddChild}
            index={index}
            options={ageGroupOptionsArr}
            childDetails={childDetails}
            childrenValues={childrenValues}
            setChildrenValues={setChildrenValues}
            form={form}
            isSubmitting={isSubmitting}
          />
        ))}
        <MobileAddChildWrapper>
          <AddChildButton onClick={onAddChild} data-cy="registration-add-child">
            + {tStepTwo('ADD_CHILD')}
          </AddChildButton>
        </MobileAddChildWrapper>
        {showCapacityWarning && (
          <CapacityWarning
            onClose={() => setShowCapacityWarning(false)}
            data-cy="registration-capacity-warning"
          />
        )}
        <BackNextButtons customNextButton onBack={onBack}>
          <NextStepButton
            color="primary"
            type="primary"
            htmlType="submit"
            onClick={handleSubmit}
          >
            {tButton('NEXT_STEP_BTN')}
          </NextStepButton>
        </BackNextButtons>
      </StyledForm>
    </Flex>
  );
};

export default ChildInformation;
